import { useState, useRef, useEffect } from 'react';

export const useAudioPermission = () => {
  const [audioAllowed, setAudioAllowed] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  // 「音声をアンロック」する関数
  const unlockAudio = () => {
    // 音声はユーザ操作(ボタン押下)で初回再生する等でアンロックが必要
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    oscillator.frequency.value = 0; // 無音
    oscillator.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.01);

    setAudioAllowed(true);
  };

  // 「音声をロック」する関数
  const lockAudio = () => {
    setAudioAllowed(false);
  };

  // 実際に音を鳴らす関数
  const playNotificationSound = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const context = audioContextRef.current;

      if (context.state === 'suspended') {
        await context.resume();
      }

      if (!audioBufferRef.current) {
        const response = await fetch('https://assets.feast.co.jp/sounds/notification-sound.mp3');
        const arrayBuffer = await response.arrayBuffer();
        audioBufferRef.current = await context.decodeAudioData(arrayBuffer);
      }

      const source = context.createBufferSource();
      source.buffer = audioBufferRef.current;
      source.connect(context.destination);
      source.start(0);
    } catch (error) {
      console.error('Play failed:', error);
    }
  };

  // SSE 接続を開始する関数
  const startSSE = (url: string): EventSource => {
    // 既存の接続とタイマーをクリーンアップ
    stopSSE();

    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    // 30秒ごとに再接続をスケジュール
    const scheduleReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('Reconnecting SSE...');
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
        const newEs = startSSE(url);
        eventSourceRef.current = newEs;
      }, 30000);
    };

    es.onopen = () => {
      console.log('SSE connection opened');
      scheduleReconnect();
    };

    es.onerror = (error) => {
      console.error('SSE connection error:', error);
      if (es.readyState === EventSource.CLOSED) {
        scheduleReconnect();
      }
    };

    return es;
  };

  // SSE 接続を終了する関数
  const stopSSE = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      stopSSE();
    };
  }, []);

  return { audioAllowed, unlockAudio, lockAudio, playNotificationSound, startSSE, stopSSE };
};